/**
 * printCasePdf
 * Generates a print-ready Arabic HTML page and opens the browser print dialog.
 * Works with either DB case data (from admin) or raw formData (from form submission).
 */

const STATUS_LABELS = {
  draft:            'مسودة',
  submitted:        'مقدَّم',
  'pending review': 'قيد المراجعة',
  documented:       'موثَّق',
  rejected:         'مرفوض',
}

function val(v) {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'نعم' : 'لا'
  if (Array.isArray(v)) return v.length ? v.join('، ') : '—'
  return String(v)
}

function row(label, value) {
  const display = val(value)
  if (display === '—' && !label) return ''
  return `
    <tr>
      <td class="label">${label}</td>
      <td class="value">${display}</td>
    </tr>`
}

function section(title, rows) {
  const content = rows.filter(Boolean).join('')
  if (!content.trim()) return ''
  return `
    <div class="section">
      <h3>${title}</h3>
      <table>${content}</table>
    </div>`
}

/**
 * Build HTML print page from DB case record (admin view).
 */
function buildFromCaseData(c) {
  const date = (d) => d ? new Date(d).toLocaleDateString('ar-SY') : '—'

  const witnesses = Array.isArray(c.witnesses)
    ? c.witnesses.map((w, i) => row(`شاهد ${i + 1}`, `${w.witness_name || ''} — ${w.witness_role || ''}`)).join('')
    : ''

  const incidentDetailsRows = c.incident_details && typeof c.incident_details === 'object'
    ? Object.entries(c.incident_details)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => row(k.replace(/_/g, ' '), v))
        .join('')
    : ''

  return {
    readableId: c.readable_id || '—',
    status: STATUS_LABELS[c.status] || c.status || '—',
    submittedAt: date(c.submitted_at),
    sections: [
      section('معلومات مقدم الطلب', [
        row('الاسم الكامل', c.name_applicant),
        row('صلة القرابة', c.relation_applicant),
        row('هاتف أساسي', c.phone_primary),
        row('هاتف ثانوي', c.phone_secondary),
        row('المنطقة', c.area_applicant),
      ]),
      section('بيانات الشهيد / المفقود', [
        row('الاسم الكامل', c.martyr_fullname),
        row('اسم الأم', c.mother_name),
        row('تاريخ الميلاد', c.birth_date),
        row('دقة التاريخ', c.birth_date_accuracy),
        row('آخر مكان إقامة', c.current_residence),
        row('مكان النفوس', c.civil_registry_place),
        row('الحالة الاجتماعية', c.marital_status),
        row('إذن نشر الصورة', c.photo_publish_consent),
      ]),
      section('تفاصيل الحادثة', [
        row('نوع الحادثة', c.incident_type),
        incidentDetailsRows,
        row('أنواع الأدلة', c.evidence_types),
      ]),
      witnesses ? `<div class="section"><h3>الشهود</h3><table>${witnesses}</table></div>` : '',
      section('الأسرة', [
        row('يوجد زوج/زوجة', c.spouse_exists),
        row('اسم الزوج/الزوجة', c.spouse_fullname),
        row('احتياجات الدعم', c.family_support_needs),
      ]),
      section('التواصل', [
        row('جهة التواصل', c.case_contact_person),
        row('صلة جهة التواصل', c.case_contact_relation),
        row('هاتف التواصل', c.contact_primary_phone),
        row('واتساب', c.contact_whatsapp),
      ]),
      section('التحقق والإقرار', [
        row('المنطقة', c.region),
        row('الحي', c.neighborhood),
        row('التوقيع', c.final_signature),
        row('تاريخ التوقيع', c.final_date ? date(c.final_date) : null),
        row('الإقرار', c.affiliation_declaration),
      ]),
    ],
  }
}

/**
 * Build HTML print page from frontend formData (after form submission).
 */
function buildFromFormData(f, readableId) {
  const witnesses = Array.isArray(f.witnesses)
    ? f.witnesses.map((w, i) => row(`شاهد ${i + 1}`, `${w.witness_name || ''} — ${w.witness_role || ''}`)).join('')
    : ''

  const incidentDetailsRows = f.incidentDetails && typeof f.incidentDetails === 'object'
    ? Object.entries(f.incidentDetails)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => row(k.replace(/_/g, ' '), v))
        .join('')
    : ''

  return {
    readableId: readableId || '—',
    status: 'مقدَّم',
    submittedAt: new Date().toLocaleDateString('ar-SY'),
    sections: [
      section('معلومات مقدم الطلب', [
        row('الاسم الكامل', f.name_applicant),
        row('صلة القرابة', f.relation_applicant),
        row('هاتف أساسي', f.phone_primary),
        row('هاتف ثانوي', f.phone_secondary),
        row('المنطقة', f.area_applicant),
      ]),
      section('بيانات الشهيد / المفقود', [
        row('الاسم الكامل', f.martyr_fullname),
        row('اسم الأم', f.mother_name),
        row('تاريخ الميلاد', f.birth_date),
        row('دقة التاريخ', f.birth_date_accuracy),
        row('آخر مكان إقامة', f.current_residence),
        row('مكان النفوس', f.civil_registry_place),
        row('الحالة الاجتماعية', f.marital_status),
        row('إذن نشر الصورة', f.photo_publish_consent),
      ]),
      section('تفاصيل الحادثة', [
        row('نوع الحادثة', f.incident_type),
        incidentDetailsRows,
        row('أنواع الأدلة', f.evidence_types),
      ]),
      witnesses ? `<div class="section"><h3>الشهود</h3><table>${witnesses}</table></div>` : '',
      section('الأسرة', [
        row('يوجد زوج/زوجة', f.spouseExists),
        row('اسم الزوج/الزوجة', f.spouse_fullname),
        row('احتياجات الدعم', f.family_support_needs),
      ]),
      section('التواصل', [
        row('جهة التواصل', f.case_contact_person),
        row('صلة جهة التواصل', f.case_contact_relation),
        row('هاتف التواصل', f.contact_primary_phone),
        row('واتساب', f.contact_whatsapp),
      ]),
      section('التحقق والإقرار', [
        row('المنطقة', f.region),
        row('الحي', f.neighborhood),
        row('التوقيع', f.final_signature),
        row('تاريخ التوقيع', f.final_date),
        row('الإقرار', f.affiliationDeclaration),
      ]),
    ],
  }
}

function buildHtml({ readableId, status, submittedAt, sections }) {
  return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <title>حالة ${readableId}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Cairo', 'Tahoma', Arial, sans-serif;
      direction: rtl;
      color: #1c1917;
      background: #fff;
      padding: 32px 40px;
      font-size: 13px;
    }
    .header {
      border-bottom: 2px solid #78350f;
      padding-bottom: 16px;
      margin-bottom: 20px;
    }
    .header h1 {
      font-size: 16px;
      font-weight: 700;
      color: #78350f;
      line-height: 1.5;
    }
    .meta {
      display: flex;
      gap: 24px;
      margin-top: 8px;
      font-size: 12px;
      color: #57534e;
    }
    .meta strong { color: #1c1917; }
    .badge {
      display: inline-block;
      font-size: 11px;
      padding: 2px 10px;
      border-radius: 999px;
      background: #d4b483;
      color: #3e2000;
      font-weight: 600;
    }
    .section {
      margin-bottom: 16px;
      page-break-inside: avoid;
    }
    .section h3 {
      font-size: 13px;
      font-weight: 700;
      color: #78350f;
      background: #fef3c7;
      padding: 5px 10px;
      border-right: 3px solid #d97706;
      margin-bottom: 6px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    td {
      padding: 4px 8px;
      border-bottom: 1px solid #e7e5e4;
      vertical-align: top;
    }
    td.label {
      width: 38%;
      color: #78716c;
      font-size: 12px;
    }
    td.value {
      font-weight: 600;
      font-size: 12px;
    }
    .footer {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px solid #d6d3d1;
      text-align: center;
      font-size: 11px;
      color: #a8a29e;
    }
    @media print {
      body { padding: 16px 20px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>المنصة الوطنية لتوثيق شهداء ومفقودي الثورة السورية المباركة</h1>
    <div class="meta">
      <span>رقم الحالة: <strong>${readableId}</strong></span>
      <span>الحالة: <span class="badge">${status}</span></span>
      <span>تاريخ التقديم: <strong>${submittedAt}</strong></span>
    </div>
  </div>

  ${sections.join('\n')}

  <div class="footer">
    صادر عن منصة shaheed-syr.com — حفظاً للذاكرة، وإحقاقاً للحق
    <br/>تاريخ الطباعة: ${new Date().toLocaleString('ar-SY')}
  </div>

  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 400);
    });
  </script>
</body>
</html>`
}

/**
 * Open a print window for a DB case record (used in admin panel).
 * @param {object} caseData - full case object from API
 */
export function printCaseFromDb(caseData) {
  const parts = buildFromCaseData(caseData)
  const html  = buildHtml(parts)
  openPrintWindow(html, caseData.readable_id)
}

/**
 * Open a print window for form data (used on success page).
 * @param {object} formData  - raw form state
 * @param {string} readableId - e.g. "DM-2026-000002"
 */
export function printCaseFromForm(formData, readableId) {
  const parts = buildFromFormData(formData, readableId)
  const html  = buildHtml(parts)
  openPrintWindow(html, readableId)
}

function openPrintWindow(html, readableId) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const win  = window.open(url, `_blank`)
  if (!win) {
    // Fallback if popup blocked: create <a> and click
    const a = document.createElement('a')
    a.href     = url
    a.download = `case_${readableId}.html`
    a.click()
  }
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}
